import { Box, Button, DialogActions, DialogContent, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import useOrderDetail from "src/hooks/orders/useOrderDetail";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import useAppGlobal from "src/context/useAppGlobal";
import { COMMOM_STATUS, EXPORT_TYPE, EXPORT_TYPE_TRANSLATETRANSLATE, orderStatusFormatted, PRINT_SIZE } from "src/constant";
import useOrderPreview from "src/hooks/orders/useOrderPreview";
import DeliveredReceipt from "./export-form/DeliveredReceipt";
import useOrderPrint from "src/hooks/orders/useOrderPrint";
import useProcessLogDetail from "src/hooks/process-log/useProcessLogDetail";
import appConfig from "src/config/env";
import OrderTable from "./OrderTable";
import useOrderScan from "src/hooks/orders/useOrderScan";
import useDebounce from "src/hooks/useDebounce";
import WaitListTable from "./WaitListTable";
import useOrderTrackingStatus from "src/hooks/orders/useOrderTrackingStatus";
import convertCurrency, { convertIntMoney } from "src/helper/convertCurrency";
import MallScanner from "src/components/shared/ScanningInput";
import useAuthGlobal from "src/context/useAuthGlobal";

export default function PrepareOrderDialog({ item, mutate: parentMutate }) {
  const { userMe } = useAuthGlobal();
  const { closeDialog } = useAppGlobal();
  const [logId, setLogId] = useState();
  const [exportTypePick, setExportTypePick] = useState(EXPORT_TYPE.WarehouseExport);
  const [itemCodes, setItemCodes] = useState(null);
  const [waitList, setWaitList] = useState([]);
  /** false: soạn hàng / scan; true: bước in (radio + preview) */
  const [isPrinted, setIsPrinted] = useState(false);
  const debounceCode = useDebounce(itemCodes, 200);

  const isReturnOrExchangeOrder = [orderStatusFormatted.ReturnRefund, orderStatusFormatted.Exchange].includes(item?.type);

  const { data: orderDetail } = useOrderDetail(item?._id, {
    warehouse: item?.warehouse,
  });
  const { data: orderPreview, isLoading } = useOrderPreview(isPrinted && item?._id, {
    warehouse: item?.warehouse,
    outputFile: exportTypePick,
  });
  const { mutate, isLoading: printLoading } = useProcessLogDetail(logId, {
    warehouse: item?.warehouse,
  });
  const { trigger: printAll } = useOrderPrint();
  const { trigger: scan } = useOrderScan(item?._id);
  const { trigger: changeStatus } = useOrderTrackingStatus(item?._id);

  useEffect(() => {
    if (debounceCode) {
      setWaitList([debounceCode?.replaceAll("Shift", "")]);
      setItemCodes(null);
    }
  }, [debounceCode]);

  useEffect(() => {
    const handleScan = async () => {
      const code = waitList[0];
      if (!code) return;

      const res = await scan({
        body: { itemCode: code, warehouse: userMe?.currentWarehouse?._id },
      });

      if (res?.success) {
        setWaitList((prev) => prev.slice(1));
      } else {
        enqueueSnackbar(
          <span>
            {res?.error?.message} <b>{code}</b>
          </span>,
          {
            variant: "warning",
            persist: true,
            action: (snackbarId) => (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setWaitList((prev) => prev.slice(1));
                  closeSnackbar(snackbarId);
                }}
              >
                Xác nhận
              </Button>
            ),
          },
        );
      }
    };

    if (waitList.length > 0) {
      handleScan();
    }
  }, [waitList.length]);

  const isFinished = () => {
    const totalOrder = orderDetail?.data?.orderDetails?.reduce((acc, curr) => acc + curr?.qty, 0);
    const currTotal = orderDetail?.data?.orderDetails?.reduce((acc, curr) => acc + (curr?.pickedQty || 0), 0);
    return totalOrder === currTotal;
  };

  const handlePrint = async () => {
    const res = await printAll({
      body: {
        orderIds: [item._id],
        pageSize: PRINT_SIZE.a6,
        warehouse: item?.warehouse,
        outputFiles: [exportTypePick],
      },
    });
    if (res?.success) {
      setLogId(res?.data?._id);
      handleGetLog(res?.data?._id);
      return enqueueSnackbar("Tiến hành xuất phiếu...", { variant: "success", className: 'notistack-confirm' });
    } else {
      return enqueueSnackbar(res?.error?.message ?? res?.error?.message, {
        variant: "error",
      });
    }
  };

  const handleGetLog = (logIdParam) => {
    const logIdToUse = logIdParam || logId;
    if (!logIdToUse) return;
    const timeOut = setInterval(async () => {
      try {
        const res = await mutate();
        if (res?.success) {
          if (res.data.status === COMMOM_STATUS.done) {
            if (Array.isArray(res.data?.urls)) {
              res.data.urls.forEach((v) => {
                window.open(`${appConfig.API_FILE}/${v}`, "_blank");
              });
            }
            clearInterval(timeOut);
            closeDialog();
          }

          if (res.data.status === COMMOM_STATUS.error) {
            enqueueSnackbar("Không xuất được đơn", { variant: "error" });
            clearInterval(timeOut);
          }
        }
      } catch (err) {
        clearInterval(timeOut);
      }
    }, 1000);
  };

  const handleConfirm = async () => {
    const res = await changeStatus({
      body: {
        trackingStatus: orderStatusFormatted.ReadyToPick,
      },
    });
    if (res?.success) {
      setExportTypePick(isReturnOrExchangeOrder ? EXPORT_TYPE.WarehouseImport : EXPORT_TYPE.WarehouseExport);
      setIsPrinted(true);
      return enqueueSnackbar("Xác nhận thành công!", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const totalProd = Array.isArray(orderDetail?.data?.orderDetails) ? orderDetail?.data?.orderDetails?.reduce((acc, curr) => acc + curr?.qty, 0) : 0;
  const isFull = orderDetail?.data?.orderDetails?.reduce((acc, curr) => acc + (curr?.pickedQty ?? 0), 0);

  return (
    <Fragment>
      <DialogContent>
        <Grid container>
          {!isPrinted && (
            <>
              <Grid
                xs={12}
                item
                display="flex"
              >
                {/* INFO */}
                <Box
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  sx={{
                    background: "var(--light-grey)",
                    p: "6px 12px",
                    borderRadius: "6px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "16px" }}
                    fontWeight="bold"
                  >
                    Thông tin khách hàng
                  </Typography>
                  <Typography
                    mt="6px"
                    variant="caption"
                  >
                    <strong>Tên KH: </strong>
                    {item?.customer?.fullName ?? "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    mt="4px"
                  >
                    <strong>SĐT: </strong>
                    {item?.customer?.phone ?? "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    mt="4px"
                  >
                    <strong>Địa chỉ: </strong>
                    {item?.recipient?.detailAddress ?? "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    mt="4px"
                  >
                    <strong>NV phụ trách: </strong>
                    {item?.sales?.fullName ?? "N/A"}
                  </Typography>
                </Box>
                {/* DETAIL */}
                <Box
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  sx={{
                    background: "var(--light-grey)",
                    p: "6px 12px",
                    borderRadius: "6px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "16px" }}
                    fontWeight="bold"
                  >
                    Thông tin đơn hàng
                  </Typography>
                  <Typography
                    mt="6px"
                    variant="caption"
                  >
                    <strong>Mã đơn hàng: </strong>
                    {item?.code ?? "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    mt="4px"
                  >
                    <strong>Tổng số sản phẩm: </strong>
                    {orderDetail?.data?.orderDetails?.length ?? 0}
                  </Typography>
                  <Typography
                    mt="6px"
                    variant="caption"
                  >
                    <strong>Tổng số lượng sản phẩm: </strong>
                    {totalProd ?? 0}
                  </Typography>
                  <Typography
                    mt="4px"
                    variant="caption"
                  >
                    <strong>Tổng giá trị đơn hàng: </strong>
                    {convertIntMoney(orderDetail?.data?.subTotal ?? 0) ?? 0} đồng
                  </Typography>
                  <Typography variant="caption">({convertCurrency(orderDetail?.data?.subTotal ?? 0)} đồng)</Typography>
                  <Typography
                    mt="4px"
                    variant="caption"
                  >
                    <strong>Ghi chú của đơn hàng: </strong>
                    {orderDetail?.data?.note ?? "N/A"}
                  </Typography>
                </Box>
              </Grid>
              {/* IMPORT PROD */}
              <Grid
                container
                spacing={2}
              >
                <Grid
                  xs={8}
                  item
                >
                  <Stack
                    gap={2}
                    mt={1}
                  >
                    <Typography
                      fontWeight="bold"
                      fontSize="16px"
                    >
                      Danh sách sản phẩm
                    </Typography>
                    <OrderTable prodList={orderDetail?.data?.orderDetails} />
                  </Stack>
                </Grid>
                <Grid
                  xs={4}
                  item
                  mt={5.5}
                >
                  <Stack
                    gap={2}
                    mt={1}
                  >
                    <MallScanner
                      disabled={totalProd === isFull}
                      getIndividualValue={setItemCodes}
                      individualValue={itemCodes}
                      label="Scan mã sản phẩm"
                      fullLabel="Scan mã sản phẩm"
                    />
                    <WaitListTable list={waitList} />
                  </Stack>
                </Grid>
              </Grid>
            </>
          )}
          {isPrinted && (
            <Grid
              xs={12}
              item
            >
              <Box sx={{ borderBottom: "1px solid var(--grey)", mb: 2 }}>
                <FormControl>
                  <RadioGroup
                    row
                    value={exportTypePick}
                    onChange={(e) => setExportTypePick(e.target.value)}
                  >
                    {isReturnOrExchangeOrder ? (
                      <>
                        <FormControlLabel
                          value={EXPORT_TYPE.WarehouseImport}
                          control={<Radio />}
                          label={EXPORT_TYPE_TRANSLATETRANSLATE[EXPORT_TYPE.WarehouseImport]}
                        />
                        <FormControlLabel
                          value={EXPORT_TYPE.DeliveredReceipt}
                          control={<Radio />}
                          disabled
                          label={EXPORT_TYPE_TRANSLATETRANSLATE[EXPORT_TYPE.DeliveredReceipt]}
                        />
                      </>
                    ) : (
                      <>
                        <FormControlLabel
                          value={EXPORT_TYPE.WarehouseExport}
                          control={<Radio />}
                          label={EXPORT_TYPE_TRANSLATETRANSLATE[EXPORT_TYPE.WarehouseExport]}
                        />
                        <FormControlLabel
                          value={EXPORT_TYPE.DeliveredReceipt}
                          control={<Radio />}
                          label={EXPORT_TYPE_TRANSLATETRANSLATE[EXPORT_TYPE.DeliveredReceipt]}
                        />
                      </>
                    )}
                  </RadioGroup>
                </FormControl>
              </Box>
              <Box
                sx={{
                  maxWidth: exportTypePick === EXPORT_TYPE.DeliveredReceipt ? PRINT_SIZE.width : "100%",
                  maxHeight: exportTypePick === EXPORT_TYPE.DeliveredReceipt ? PRINT_SIZE.height : "100%",
                  width: exportTypePick === EXPORT_TYPE.DeliveredReceipt ? "90%" : "100%",
                  m: "0 auto",
                }}
              >
                <DeliveredReceipt
                  isLoading={isLoading}
                  html={orderPreview}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: "24px", flexDirection: "column" }}>
        {!isPrinted ? (
          <Button
            onClick={handleConfirm}
            disabled={!isFinished()}
            fullWidth
            variant="contained"
          >
            Xác nhận
          </Button>
        ) : (
          <Button
            onClick={handlePrint}
            disabled={printLoading}
            fullWidth
            variant="contained"
          >
            In đơn hàng
          </Button>
        )}
      </DialogActions>
    </Fragment>
  );
}
